/**
 * Route admin `/tutor/admin/connexions` — « Paramètres de connexion ».
 *
 *   1. Comptes de diffusion (YouTube, TikTok, Instagram, Facebook, LinkedIn) :
 *      relié ou non, « Tester » (essai réel, rien n'est publié), « Relier » —
 *      la connexion revient ici et se teste toute seule ;
 *   2. Applications développeur : identifiants des Apps (Meta, LinkedIn,
 *      TikTok, Google), à régler une fois ;
 *   3. Clés du serveur (OpenAI, Anthropic, Stripe, SMTP, Firebase…) :
 *      configurées ou manquantes, jamais affichées, « Tester », et comment en
 *      changer une (secrets Supabase).
 */

import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  ExternalLink,
  Facebook,
  Instagram,
  KeyRound,
  Link2,
  Linkedin,
  Loader2,
  PlugZap,
  Video,
  Youtube,
} from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { SocialAppConfigsSection } from '@/features/connections/social-app-configs';
import {
  DIFFUSION_ACCOUNTS,
  KEY_GROUP_LABEL,
  oauthUrl,
  rememberOAuthReturn,
  clearOAuthResult,
  peekOAuthResult,
  supabaseSecretsUrl,
  useConnectionsStatus,
  useDiffusionAccounts,
  useTestConnection,
  youtubeNeedsPlaylistScope,
  type AccountPlatform,
  type ConnectionTestResult,
  type DiffusionAccount,
  type OAuthReturn,
  type ServerKeyStatus,
} from '@/lib/admin/connections';

const ICON: Record<AccountPlatform, typeof Youtube> = {
  youtube: Youtube,
  tiktok: Video,
  instagram: Instagram,
  facebook: Facebook,
  linkedin: Linkedin,
};

const btn =
  'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-xl px-3 text-sm font-semibold transition-colors disabled:opacity-50';
const primaryBtn = cn(btn, 'bg-primary-600 hover:bg-primary-700 text-white');
const secondaryBtn = cn(btn, 'border-ink-200 text-ink-700 hover:bg-ink-50 border bg-white');
const card = 'border-ink-200 rounded-2xl border bg-white p-4';

const dateFr = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export function TutorAdminConnectionsPage() {
  const accounts = useDiffusionAccounts();
  const status = useConnectionsStatus();
  const test = useTestConnection();
  const [results, setResults] = useState<Record<string, ConnectionTestResult>>({});
  const [running, setRunning] = useState<string | null>(null);
  // lu sans l'effacer (StrictMode appelle l'initialiseur deux fois), effacé ensuite
  const [back] = useState<OAuthReturn | null>(() => peekOAuthResult());
  const autoTested = useRef(false);
  useEffect(() => clearOAuthResult(), []);

  // `mutateAsync` (et non les rappels de `mutate`) : le résultat arrive même si
  // le composant s'est réabonné entre-temps (StrictMode, test lancé au montage)
  const run = (target: string) => {
    setRunning(target);
    test
      .mutateAsync(target)
      .then(
        (r) => setResults((cur) => ({ ...cur, [target]: r })),
        (e: Error) =>
          setResults((cur) => ({ ...cur, [target]: { target, ok: false, message: e.message } })),
      )
      .finally(() => setRunning(null));
  };

  // retour d'une connexion : on vérifie tout de suite qu'elle marche
  useEffect(() => {
    if (!back || back.ok === false || autoTested.current) return;
    autoTested.current = true;
    run(back.platform);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [back]);

  const rows = new Map((accounts.data ?? []).map((a) => [a.platform, a]));
  const backLabel = back ? DIFFUSION_ACCOUNTS.find((a) => a.id === back.platform)?.label : null;

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-4 pb-16 sm:p-6">
      <header>
        <h1 className="font-display text-ink-900 flex items-center gap-2 text-[22px] font-extrabold">
          <PlugZap className="text-primary-600 h-6 w-6" aria-hidden />
          Paramètres de connexion
        </h1>
        <p className="text-ink-600 mt-1 text-[14px]">
          Les comptes où partent tes vidéos et tes articles, les identifiants des Apps, et les clés
          du serveur. Les tests sont réels mais ne publient rien.
        </p>
      </header>

      {back && (
        <p
          role={back.ok === false ? 'alert' : 'status'}
          className={cn(
            'rounded-xl px-3 py-2 text-[13px]',
            back.ok === false ? 'bg-error-50 text-error-600' : 'bg-primary-50 text-ink-800',
          )}
        >
          {back.ok === false
            ? `Connexion à ${backLabel} échouée${back.message ? ` : ${back.message}` : '.'}`
            : `Retour de ${backLabel} : je vérifie la connexion ci-dessous.`}
        </p>
      )}

      <section aria-labelledby="comptes" className="space-y-3">
        <h2 id="comptes" className="font-display text-ink-900 text-[17px] font-extrabold">
          Comptes de diffusion
        </h2>
        {accounts.error && !accounts.data && (
          <p role="alert" className="text-error-600 text-[13px]">
            {accounts.error.message}
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          {DIFFUSION_ACCOUNTS.map((a) => (
            <AccountCard
              key={a.id}
              platform={a.id}
              label={a.label}
              uses={a.uses}
              row={rows.get(a.id) ?? null}
              loading={accounts.isLoading}
              result={results[a.id] ?? null}
              testing={running === a.id}
              busy={running !== null}
              onTest={() => run(a.id)}
            />
          ))}
        </div>
      </section>

      <section aria-labelledby="cles" className="space-y-3">
        <h2 id="cles" className="font-display text-ink-900 text-[17px] font-extrabold">
          Clés du serveur
        </h2>
        <ServerKeys
          keys={status.data?.keys ?? null}
          loading={status.isLoading}
          error={status.data ? null : (status.error?.message ?? null)}
          results={results}
          running={running}
          onTest={run}
        />
      </section>

      <section aria-labelledby="apps" className="space-y-3">
        <h2 id="apps" className="font-display text-ink-900 text-[17px] font-extrabold">
          Applications développeur
        </h2>
        <details className={cn(card, 'group')}>
          <summary className="text-ink-800 flex min-h-11 cursor-pointer select-none items-center gap-2 text-sm font-semibold">
            <KeyRound className="text-ink-500 h-4 w-4" aria-hidden />
            Identifiants des Apps Meta, LinkedIn, TikTok et Google (à régler une fois)
          </summary>
          <p className="text-ink-600 mb-4 mt-2 text-[13px]">
            Chaque réseau demande une « App » de développeur (client et secret). Si tu changes le
            client ou le secret, relie ensuite les comptes à nouveau.
          </p>
          <SocialAppConfigsSection envStatus={status.data?.apps} />
        </details>
      </section>
    </div>
  );
}

function ResultLine({ result }: { result: ConnectionTestResult }) {
  return (
    <div
      role={result.ok ? 'status' : 'alert'}
      className={cn('text-[13px]', result.ok ? 'text-success-600' : 'text-error-600')}
    >
      <p className="flex items-start gap-1.5">
        {result.ok ? (
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        )}
        <span>{result.message}</span>
      </p>
      {result.detail && (
        <details className="text-ink-500 mt-1 text-[12px]">
          <summary className="cursor-pointer select-none">Réponse du service</summary>
          <p className="bg-ink-50 mt-1 break-words rounded-lg p-2 font-mono text-[11px]">
            {result.detail}
          </p>
        </details>
      )}
    </div>
  );
}

function AccountCard({
  platform,
  label,
  uses,
  row,
  loading,
  result,
  testing,
  busy,
  onTest,
}: {
  platform: AccountPlatform;
  label: string;
  uses: string;
  row: DiffusionAccount | null;
  loading: boolean;
  result: ConnectionTestResult | null;
  testing: boolean;
  busy: boolean;
  onTest: () => void;
}) {
  const Icon = ICON[platform];
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const link = async () => {
    setLinking(true);
    setLinkError(null);
    try {
      const url = await oauthUrl(platform);
      rememberOAuthReturn(platform);
      window.location.assign(url);
    } catch (e) {
      setLinkError((e as Error).message);
      setLinking(false);
    }
  };

  return (
    <article className={cn(card, 'flex flex-col gap-3')} data-testid={`account-${platform}`}>
      <div className="flex items-start gap-3">
        <span className="bg-ink-50 text-ink-700 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-ink-900 text-[15px] font-extrabold">{label}</h3>
          <p className="text-ink-500 text-[12px]">{uses}</p>
        </div>
      </div>
      <p className="text-[13px]">
        {loading ? (
          <span className="text-ink-500">Chargement…</span>
        ) : row ? (
          <span className="text-ink-800">
            <span className="text-success-600 font-semibold">Relié</span>
            {row.channel_title ? ` : ${row.channel_title}` : ''}
            <span className="text-ink-500"> · depuis le {dateFr(row.updated_at)}</span>
          </span>
        ) : (
          <span className="font-semibold text-amber-700">Pas relié</span>
        )}
      </p>
      {youtubeNeedsPlaylistScope(row) && (
        <p className="bg-warning-50 text-ink-800 rounded-lg px-2.5 py-1.5 text-[13px] font-semibold">
          Relie à nouveau la chaîne : il manque le droit de créer les playlists. Les Shorts
          d’animations ne sont pas rangés par niveau, matière et domaine tant que ce n’est pas fait.
        </p>
      )}
      {result && <ResultLine result={result} />}
      {linkError && (
        <p role="alert" className="text-error-600 text-[13px]">
          {linkError}
        </p>
      )}
      <div className="mt-auto flex flex-wrap gap-2">
        {row && (
          <button
            type="button"
            className={secondaryBtn}
            disabled={busy}
            onClick={onTest}
            aria-label={`Tester ${label}`}
          >
            {testing ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <PlugZap className="h-4 w-4" aria-hidden />
            )}
            {testing ? 'Test…' : 'Tester'}
          </button>
        )}
        <button
          type="button"
          className={row ? secondaryBtn : primaryBtn}
          disabled={linking}
          onClick={() => void link()}
          aria-label={`${row ? 'Relier à nouveau' : 'Relier'} ${label}`}
        >
          {linking ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Link2 className="h-4 w-4" aria-hidden />
          )}
          {row ? 'Relier à nouveau' : 'Relier'}
        </button>
      </div>
    </article>
  );
}

function CopyCommand({ name }: { name: string }) {
  const [copied, setCopied] = useState(false);
  const command = `supabase secrets set ${name}=…`;
  return (
    <div className="flex items-center gap-2">
      <code className="bg-ink-50 text-ink-800 min-w-0 flex-1 truncate rounded-lg px-2 py-1.5 font-mono text-[12px]">
        {command}
      </code>
      <button
        type="button"
        className={cn(secondaryBtn, 'min-h-9 px-2.5 text-[12px]')}
        aria-label={`Copier la commande pour ${name}`}
        onClick={() => {
          void navigator.clipboard?.writeText(command).then(
            () => setCopied(true),
            () => setCopied(false),
          );
        }}
      >
        <Copy className="h-3.5 w-3.5" aria-hidden />
        {copied ? 'Copiée' : 'Copier'}
      </button>
    </div>
  );
}

function KeyChip({ k }: { k: ServerKeyStatus }) {
  if (!k.set)
    return (
      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-800">
        Manquante
      </span>
    );
  return (
    <span className="bg-success-50 text-success-600 rounded-full px-2 py-0.5 text-[11px] font-bold">
      {k.kind === 'setting' ? 'Réglée' : 'Configurée'}
      {k.hint ? ` · ${k.hint}` : ''}
    </span>
  );
}

function ServerKeys({
  keys,
  loading,
  error,
  results,
  running,
  onTest,
}: {
  keys: ServerKeyStatus[] | null;
  loading: boolean;
  error: string | null;
  results: Record<string, ConnectionTestResult>;
  running: string | null;
  onTest: (target: string) => void;
}) {
  const secrets = supabaseSecretsUrl(import.meta.env.VITE_SUPABASE_URL as string | undefined);
  if (loading)
    return (
      <p className="text-ink-500 flex items-center gap-2 text-[13px]">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Lecture des clés…
      </p>
    );
  if (error || !keys)
    return (
      <p role="alert" className="text-error-600 text-[13px]">
        {error ?? 'Clés illisibles.'}
      </p>
    );
  const groups = [...new Set(keys.map((k) => k.group))];
  const missing = keys.filter((k) => !k.set).length;

  return (
    <div className="space-y-4">
      <div className={cn(card, 'text-ink-700 space-y-2 text-[13px]')}>
        <p>
          Ces clés sont des secrets Supabase : elles ne sont jamais affichées ici (au plus leurs 4
          derniers caractères, pour les reconnaître).{' '}
          {missing > 0 ? `${missing} manquante${missing > 1 ? 's' : ''}.` : 'Toutes réglées.'}
        </p>
        <p>
          Pour en changer une : dans Supabase, ou dans le dossier du projet avec la commande
          indiquée (« … » = la nouvelle valeur). Les fonctions la prennent aussitôt, sans
          redéploiement.
        </p>
        {secrets && (
          <a
            href={secrets}
            target="_blank"
            rel="noreferrer"
            className="text-primary-700 inline-flex min-h-11 items-center gap-1 font-semibold underline"
          >
            Ouvrir les secrets dans Supabase <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
        )}
        <p className="text-ink-500 text-[12px]">
          Le worker vidéo de ton ordinateur a ses propres réglages : apps/video-worker/.env.
        </p>
      </div>

      {groups.map((g) => (
        <div key={g} className={card}>
          <h3 className="font-display text-ink-900 mb-2 text-[14px] font-extrabold">
            {KEY_GROUP_LABEL[g] ?? g}
          </h3>
          <ul className="divide-ink-100 divide-y">
            {keys
              .filter((k) => k.group === g)
              .map((k) => {
                const result = k.test ? results[k.test] : undefined;
                return (
                  <li key={k.name} className="space-y-1.5 py-2.5" data-testid={`key-${k.name}`}>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-ink-900 text-[14px] font-semibold">{k.label}</span>
                      <KeyChip k={k} />
                    </div>
                    <p className="text-ink-500 text-[12px]">{k.purpose}</p>
                    <p className="text-ink-500 break-all font-mono text-[11px]">
                      {k.name}
                      {k.value ? ` = ${k.value}` : ''}
                    </p>
                    {result && <ResultLine result={result} />}
                    <div className="flex flex-wrap items-center gap-2">
                      {k.test && (
                        <button
                          type="button"
                          className={cn(secondaryBtn, 'min-h-9 text-[13px]')}
                          disabled={running !== null}
                          onClick={() => onTest(k.test!)}
                          aria-label={
                            k.test === 'push'
                              ? 'M’envoyer une notification de test'
                              : `Tester ${k.label}`
                          }
                        >
                          {running === k.test ? (
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                          ) : (
                            <PlugZap className="h-4 w-4" aria-hidden />
                          )}
                          {k.test === 'smtp'
                            ? 'M’envoyer un email de test'
                            : k.test === 'push'
                              ? 'M’envoyer une notification de test'
                              : 'Tester'}
                        </button>
                      )}
                      <details className="min-w-0 flex-1">
                        <summary className="text-primary-700 cursor-pointer select-none text-[12px] font-semibold">
                          Changer
                        </summary>
                        <div className="mt-1.5">
                          <CopyCommand name={k.name} />
                        </div>
                      </details>
                    </div>
                  </li>
                );
              })}
          </ul>
        </div>
      ))}
    </div>
  );
}
